import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Play, Eye, Pencil } from 'lucide-react';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { EditorSelectionRange } from '@/types/aiWorkspace';
import type { DatabaseEngineId } from '@/core/engines';
import {
  isExecutableSelect,
  isExecutableStatement,
  isLikelyCompleteStatement,
} from '../../common/utils/peekResultsDetection';
import type { VqeUnavailableReason } from '../../visual-query-editor/compact/vqeEditAffordance';
import { VqeUnavailableIndicator } from '../../visual-query-editor/compact/VqeUnavailableIndicator';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';

interface CursorStatementGutterActionsProps {
  editorRef: React.RefObject<SqlMonacoHandle | null>;
  /** Source range of the statement under the cursor (null = no cursor target). */
  statementRange: EditorSelectionRange | null;
  /** Trimmed statement text under the cursor. */
  statementText: string;
  /**
   * The user's active selection range, if any. When present and runnable,
   * the gutter buttons anchor to the selection's first line and Execute /
   * Peek act on the selection text instead of the cursor's statement.
   */
  selectionRange?: EditorSelectionRange | null;
  /** Selection text — drives selection-mode visibility. */
  selectionText?: string;
  /** Full editor content — used to detect "selection covers everything". */
  editorContent?: string;
  engineId?: DatabaseEngineId;
  isExecuting: boolean;
  isPeekExecuting?: boolean;
  /**
   * Whether the cursor-mode Execute / Peek buttons may render. These stay
   * 2+-statement-only (single-statement buffers run via the toolbar / F5). The
   * edit pencil is NOT gated on this — it renders for the cursor statement in
   * every buffer. Selection mode is unaffected (Execute / Peek always act on a
   * runnable selection). Defaults to `true` so existing call sites / tests keep
   * their behavior.
   */
  showRunActions?: boolean;
  /** Run the chosen SQL (selection text in selection mode, statement text otherwise). */
  onExecuteStatement?: (sql: string) => void;
  onExecuteSelection?: (sql: string) => void;
  /** Peek the chosen SQL. The optional `anchorLine` lets cursor mode pin the
   *  Peek overlay to the statement's end line; selection mode passes nothing
   *  so the Peek hook derives the anchor from the active selection. */
  onPeekStatement?: (sql: string, anchorLine?: number) => void;
  onPeekSelection?: (sql: string) => void;
  /**
   * When true, the statement is VQE-editable (SELECT/DML or DDL) so a third
   * pencil "Edit" gutter button renders below Play/Peek. In cursor mode that is
   * the cursor's statement; in selection mode the host only sets it when the
   * selection covers exactly one whole statement (editing always targets a
   * whole statement, never a fragment of one).
   */
  canEdit?: boolean;
  /** Open the minimal edit affordance (compact popover / full DDL editor). */
  onEditStatement?: () => void;
  /**
   * Why the cursor statement can't be edited visually, when the pencil is
   * hidden (procedural / mixed set-op / genuine parse-error). Non-null renders
   * a passive teaching indicator in the gutter where the pencil would be — the
   * never-silent honesty floor (VQE Polish). Mutually exclusive with `canEdit`.
   */
  unavailableReason?: VqeUnavailableReason;
}

type Mode = 'selection' | 'cursor';

interface ResolvedTarget {
  mode: Mode;
  anchorLine: number;
  endLine: number;
  text: string;
  canExecute: boolean;
  canPeek: boolean;
}

/**
 * Compact gutter alternative to `CursorStatementActions`. Renders when the
 * floating Execute / Peek bar setting is OFF: two square icon buttons stacked
 * vertically and anchored to the left edge of the editor — i.e. *to the left
 * of the line numbers*. Visually sits in Monaco's glyph margin area.
 *
 * Two modes:
 * - **Selection mode** (preferred when the user has an active, runnable
 *   selection): anchor at the selection's first line, Execute / Peek act on
 *   the selection. Same runnability gates as `SelectionFloatingActions`
 *   (`isExecutableStatement` for Execute, `isExecutableSelect` for Peek;
 *   Execute hides if the selection covers the whole editor — that's already
 *   what the toolbar Execute does).
 * - **Cursor mode** (fallback when there's no selection): anchor at the
 *   cursor's statement first line, Execute / Peek act on that statement.
 *   Same gates as `CursorStatementActions`.
 *
 * Each button matches Monaco's line height, so the stack always spans two
 * rows even when the underlying statement / selection is just one line tall.
 */
export function CursorStatementGutterActions({
  editorRef,
  statementRange,
  statementText,
  selectionRange,
  selectionText = '',
  editorContent = '',
  engineId,
  isExecuting,
  isPeekExecuting,
  showRunActions = true,
  onExecuteStatement,
  onExecuteSelection,
  onPeekStatement,
  onPeekSelection,
  canEdit = false,
  onEditStatement,
  unavailableReason = null,
}: CursorStatementGutterActionsProps) {
  // O(document) trim hoisted out of the target memo: the memo below re-runs
  // on every settled selection/caret update, and re-trimming the whole buffer
  // there made each of those updates pay for document size. The content only
  // changes on edits.
  const editorContentTrimmed = useMemo(() => editorContent.trim(), [editorContent]);

  const target = useMemo<ResolvedTarget | null>(() => {
    // Selection mode wins when the user has an active selection that parses
    // as runnable SQL. We never *both* anchor to a selection and run the
    // cursor's statement — the visible target the user is acting on must
    // match what Execute / Peek do on click.
    const selTrim = selectionText.trim();
    if (selTrim && selectionRange) {
      const isWholeEditor = selTrim === editorContentTrimmed;
      const canExecute = isExecutableStatement(selTrim) && !isWholeEditor;
      const canPeek = isExecutableSelect(selTrim);
      // `canEdit` here means the selection IS one whole statement.
      if (canExecute || canPeek || canEdit) {
        return {
          mode: 'selection',
          anchorLine: selectionRange.startLineNumber,
          endLine: selectionRange.endLineNumber,
          text: selectionText,
          canExecute,
          canPeek,
        };
      }
      // Selection exists but isn't runnable — hide entirely. Showing the
      // cursor-mode buttons here would be confusing because they'd run the
      // statement underneath the user's selection, which isn't what the
      // selected text represents.
      return null;
    }

    // No selection → cursor mode.
    if (statementRange && statementText) {
      const looksComplete = isLikelyCompleteStatement(statementText, engineId);
      // A statement that can't be edited visually still earns a gutter presence:
      // the never-silent teaching indicator anchors here even when the statement
      // is incomplete / non-runnable (the pencil is hidden — this explains why).
      const hasReason = !!unavailableReason;
      if (!looksComplete && !hasReason) return null;
      // Execute / Peek in cursor mode stay 2+-statement-only (`showRunActions`),
      // and never fire for an incomplete statement. On a single-statement buffer
      // the target is still produced (so the edit pencil / teaching indicator can
      // render) but with no run actions; the bail-out below drops it entirely
      // when there's also nothing to edit and no reason to teach.
      const canExecute = looksComplete && showRunActions;
      const canPeek = looksComplete && showRunActions && isExecutableSelect(statementText);
      if (!canExecute && !canPeek && !canEdit && !hasReason) return null;
      return {
        mode: 'cursor',
        anchorLine: statementRange.startLineNumber,
        endLine: statementRange.endLineNumber,
        text: statementText,
        canExecute,
        canPeek,
      };
    }

    return null;
  }, [selectionText, selectionRange, editorContentTrimmed, statementRange, statementText, engineId, showRunActions, canEdit, unavailableReason]);

  const enabled = !!target && !isExecuting;

  const [layout, setLayout] = useState<{ top: number; lineHeight: number } | null>(null);

  const computeLayout = useCallback((): { top: number; lineHeight: number } | null => {
    if (!enabled || !target) return null;
    const editor = editorRef.current;
    if (!editor) return null;
    const pos = editor.getPixelPositionForPosition(target.anchorLine, 1);
    if (!pos) return null;
    const editorHeight = editor.getLayoutHeight();
    if (editorHeight !== null && (pos.top < 0 || pos.top > editorHeight)) {
      return null;
    }
    return { top: pos.top, lineHeight: pos.height };
  }, [enabled, editorRef, target]);

  useEffect(() => {
    setLayout(computeLayout());
  }, [computeLayout]);

  useEffect(() => {
    if (!enabled) return;
    const editor = editorRef.current;
    if (!editor) return;
    const refresh = () => setLayout(computeLayout());
    const offScroll = editor.onDidScrollChange(refresh);
    const offLayout = editor.onDidLayoutChange(refresh);
    return () => {
      offScroll();
      offLayout();
    };
  }, [enabled, editorRef, computeLayout]);

  const handleExecuteClick = useCallback(() => {
    if (!target) return;
    if (target.mode === 'selection') {
      onExecuteSelection?.(target.text);
    } else {
      onExecuteStatement?.(target.text);
    }
  }, [target, onExecuteSelection, onExecuteStatement]);

  const handlePeekClick = useCallback(() => {
    if (!target || isPeekExecuting) return;
    if (target.mode === 'selection') {
      onPeekSelection?.(target.text);
    } else {
      onPeekStatement?.(target.text, target.endLine);
    }
  }, [target, isPeekExecuting, onPeekSelection, onPeekStatement]);

  const handleEditClick = useCallback(() => {
    onEditStatement?.();
  }, [onEditStatement]);

  // `displayMode` drives the visible outline style. It lags behind
  // `target.mode` by a settle window so a mouse drag through partially-
  // runnable SQL doesn't flicker between cursor (solid) and selection
  // (dashed), and updates at the animation's invisible midpoint so the
  // style swap snaps while the outline is collapsed. Visibility itself
  // (target null vs not) is not debounced — the button still appears or
  // disappears immediately. When the button first becomes visible after
  // being hidden, it syncs to the current mode immediately (no pulse —
  // the appearance itself is the visual feedback).
  const executeButtonRef = useRef<HTMLButtonElement>(null);
  const currentExecuteMode: Mode | null = target?.canExecute ? target.mode : null;
  const [displayMode, setDisplayMode] = useState<Mode>(currentExecuteMode ?? 'cursor');
  const wasVisibleRef = useRef(currentExecuteMode !== null);
  useEffect(() => {
    if (currentExecuteMode === null) {
      wasVisibleRef.current = false;
      return;
    }
    if (!wasVisibleRef.current) {
      wasVisibleRef.current = true;
      if (currentExecuteMode !== displayMode) setDisplayMode(currentExecuteMode);
      return;
    }
    if (currentExecuteMode === displayMode) return;
    let swapTimer: ReturnType<typeof setTimeout> | undefined;
    const settleTimer = setTimeout(() => {
      const el = executeButtonRef.current;
      if (el) {
        el.classList.remove('animate-execute-outline-pulse');
        void el.offsetWidth;
        el.classList.add('animate-execute-outline-pulse');
      }
      swapTimer = setTimeout(() => {
        setDisplayMode(currentExecuteMode);
      }, 125);
      // Same 80ms settle / 125ms midpoint beats as the toolbar Execute button
      // (QueryToolbar.tsx) so both Execute surfaces flip in lockstep.
    }, 80);
    return () => {
      clearTimeout(settleTimer);
      if (swapTimer) clearTimeout(swapTimer);
    };
  }, [currentExecuteMode, displayMode]);

  if (!enabled || !target || layout === null) return null;

  // Match button width to line height — square buttons read as gutter glyphs.
  // Icon scales to ~half the line height so it stays comfortably inside the
  // button bounds on any font-size / line-height combination.
  const buttonSize = layout.lineHeight;
  const iconSize = Math.max(8, Math.round(buttonSize * 0.5));

  return (
    <div
      // Inset 2 px from the editor's left edge so the offset outline
      // (and its mid-pulse expansion) isn't clipped by the editor
      // container's `overflow-hidden`.
      className="absolute z-20 pointer-events-auto flex flex-col items-stretch"
      style={{ top: layout.top, left: 2 }}
      data-testid="cursor-statement-gutter-actions"
      data-mode={target.mode}
    >
      {target.canExecute && (
        <button
          ref={executeButtonRef}
          type="button"
          onClick={handleExecuteClick}
          style={{
            height: buttonSize,
            width: buttonSize,
            // Tighter rest gap than the toolbar (the gutter sits flush with
            // the editor's left edge, so big offsets get clipped).
            ['--exec-outline-offset' as string]: '1px',
          }}
          // Outline is always visible — solid in cursor-statement mode and
          // dashed in selection mode (so the user can tell at a glance which
          // SQL the button will run). The visible mode is `displayMode`,
          // which lags behind `target.mode` so the styling settles after a
          // drag rather than flickering. On a mode flip the outline pulses
          // inward to invisible and back out via
          // `animate-execute-outline-pulse`; the style swap happens at the
          // collapsed midpoint. Icon is keyed by `displayMode` so the
          // `animate-icon-zoom` pulse fires alongside.
          //
          // Fill is `--primary-alt` (U10), not `--primary` — this button
          // now matches the query-toolbar Execute button and the inline
          // floating-bar Execute buttons (CursorStatementActions /
          // SelectionFloatingActions), all sharing one grey fill token
          // instead of three different treatments. The mode-indicator
          // outline below stays BLUE (`--primary`) — only the fill moved,
          // per the token's "structure stays blue" rule. See src/index.css's
          // --primary-alt comment and task-U10-report.md.
          //
          // U15 — full-strength `bg-primary-alt` (not `/85`), matching the
          // toolbar exactly: the toolbar's fill was never diluted, so this
          // button (and its inline siblings) read visibly dimmer next to it.
          // Hover is PINNED to `/90` — not omitted — per this wave's
          // recurring Tailwind-specificity trap (a scoped `hover:` utility
          // silently outranks a plain state class, see QueryToolbar.tsx's
          // comment at the top of this button's className); omitting the
          // hover class would also drop `cursor-pointer`.
          className={`flex items-center justify-center rounded-sm
            bg-primary-alt text-primary-alt-foreground hover:bg-primary-alt/90
            backdrop-blur-sm shadow-sm
            outline outline-1 outline-offset-[1px] ${
              displayMode === 'selection'
                ? 'outline-dashed outline-primary/70'
                : 'outline-solid outline-primary/40'
            }
            transition-colors`}
          data-testid="cursor-statement-gutter-execute-button"
          data-execute-mode={displayMode}
          title={target.mode === 'selection' ? 'Execute selection' : 'Execute this statement'}
        >
          {/* U15 — orange glyph matches the toolbar's fill-aware (not
              theme-aware) pair: `text-energy-on-dark-fill` for light theme's
              dark-ish `--primary-alt` fill, `dark:text-energy-on-light-fill`
              for dark theme's light-ish fill. See QueryToolbar.tsx's Execute
              icon comment and src/index.css's --energy-on-*-fill tokens for
              the full measured writeup (3.77:1 / 3.44:1 vs fill, both
              clearing the 3:1 non-text minimum). */}
          <Play
            key={displayMode}
            style={{ height: iconSize, width: iconSize }}
            className="animate-icon-zoom text-energy-on-dark-fill dark:text-energy-on-light-fill"
          />
        </button>
      )}

      {target.canPeek && (
        <button
          type="button"
          onClick={handlePeekClick}
          disabled={isPeekExecuting}
          style={{ height: buttonSize, width: buttonSize }}
          className="flex items-center justify-center rounded-sm
            bg-background/85 text-foreground hover:bg-accent
            border border-border backdrop-blur-sm shadow-sm
            transition-colors disabled:opacity-70"
          data-testid="cursor-statement-gutter-peek-button"
          data-peek-mode={target.mode}
          title={target.mode === 'selection' ? 'Peek selection results' : "Peek this statement's results"}
        >
          {isPeekExecuting ? (
            <RandomDotmSpinner size={iconSize} />
          ) : (
            <Eye
              key={target.mode}
              style={{ height: iconSize, width: iconSize }}
              className="animate-icon-zoom"
            />
          )}
        </button>
      )}

      {/* Visual-edit affordance — the cursor's statement, or in selection
          mode the one statement the selection covers exactly (see `canEdit`).
          Opens the Compact VQE popover for SELECT/DML, or the full Visual
          editor for DDL. */}
      {canEdit && (
        <button
          type="button"
          onClick={handleEditClick}
          style={{ height: buttonSize, width: buttonSize }}
          className="flex items-center justify-center rounded-sm
            bg-background/85 text-foreground hover:bg-accent
            border border-border backdrop-blur-sm shadow-sm
            transition-colors"
          data-testid="vqe-edit-gutter-button"
          title="Edit this statement visually"
          aria-label="Edit this statement visually"
        >
          <Pencil style={{ height: iconSize, width: iconSize }} className="animate-icon-zoom" />
        </button>
      )}

      {/* Never-silent teaching indicator — stands in for the hidden pencil when
          the cursor statement can't be edited visually (procedural / mixed
          set-op / genuine parse-error). Passive, non-clickable; the tooltip
          names the construct, the reason, and the next step. Cursor mode only
          (editing / this explanation target the whole statement). */}
      {unavailableReason && target.mode === 'cursor' && (
        <VqeUnavailableIndicator
          reason={unavailableReason}
          variant="gutter"
          sizePx={buttonSize}
          iconSize={iconSize}
        />
      )}
    </div>
  );
}
